<template>
	<RotationLayout :rotation="rotation" :subtitle="trans('rotations.title')">
		<template #actions>
			<SecondaryButton v-if="rotation.meeting" :color="Color.Blue" @click.stop="copyTextToClipboard(rotation.meeting.link)">
				<i class="fa-solid fa-link mr-2"/>
				{{ trans('rotations.copy_link') }}
			</SecondaryButton>
			<PrimaryButton v-if="rotation.all_participants_resolved" :color="Color.Purple" @click.stop="confirmParticipants">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('rotations.actions.confirm') }}
			</PrimaryButton>
			<PrimaryButton v-if="rotation.can_mark_as_admissible" :color="Color.Purple" @click.stop="markAsAdmissible">
				<i class="fa-solid fa-vote-yea mr-2"/>
				{{ trans('rotations.actions.validate') }}
			</PrimaryButton>
			<PrimaryButton :color="Color.Green" @click.stop="update">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<Card :title="rotation.election_period">
			<template #badges>
				<Badge :color="badgeColor" active>{{ trans(`rotations.status.${rotation.status}`) }}</Badge>
				<i v-if="rotation.online" class="fa-solid fa-video text-gray-400"/>
			</template>
			<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
				<template v-if="!rotation.online">
					<LabeledObject>
						<template #label>{{ trans('rotations.requests.venue') }}</template>
						<input v-model="rotation.venue" type="text">
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('rotations.requests.classroom') }}</template>
						<input v-model="rotation.classroom" type="text">
					</LabeledObject>
				</template>
				<LabeledObject>
					<template #label>{{ trans('rotations.requests.begins_at') }}</template>
					<IconDateTimeInput v-model="rotation.begins_at" icon-class="fa-solid fa-hourglass-start" full/>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('rotations.requests.ends_at') }}</template>
					<IconDateTimeInput v-model="rotation.ends_at" icon-class="fa-solid fa-hourglass-end" full/>
				</LabeledObject>
			</div>
		</Card>
	</RotationLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import RotationLayout from '@/Layouts/RotationLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {computed, ComputedRef, defineProps, type Ref, ref} from 'vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {getRotationStatusColor} from '@/Helpers/Association/Representatives/rotation'
import {copyTextToClipboard, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	rotation:Rotation,
}>()

const working:Ref<boolean> = ref(false)
const rotation:Ref<Rotation> = ref(props.rotation)

const badgeColor:ComputedRef<Color> = computed(():Color => {
	return getRotationStatusColor(rotation.value)
})

function update():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.update', rotation.value.id), {
			action: 'save',
			...rotation.value,
		}).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function confirmParticipants():void{
	if(!working.value && rotation.value.all_participants_resolved && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.update', rotation.value.id), {
			action: 'confirm',
		}).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function markAsAdmissible():void{
	if(!working.value && rotation.value.can_mark_as_admissible && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.update', rotation.value.id), {
			action: 'admit',
		}).then(() => {
			showSuccess(trans('general.updated'))
			rotation.value.is_confirmed = false
			rotation.value.is_admissible = true
			rotation.value.can_mark_as_admissible = false
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>